import React, { useState, useEffect, useRef } from "react";
import axios from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  BadgeCheck,
  Clock,
  CheckCircle,
  UserCheck,
  Truck,
  PackageCheck,
  XCircle,
  Loader,
  ShoppingBag,
  MapPin,
  Star,
  X,
  Bike,
  ChevronDown,
  ChevronUp,
  Navigation,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import MobileBottomBar from "../../components/MobileBottomBar";
import OrderChat from "../../components/OrderChat";
import RyderLiveMap from "../../components/RyderLiveMap";
import { getImageUrl } from "../../utils/getImageUrl";

const SOCKET_URL = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/api\/?$/, "");

export default function MyOrders() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [openOrderId, setOpenOrderId] = useState(() => new URLSearchParams(window.location.search).get("chat"));
  const [chatTab, setChatTab] = useState(() =>
    new URLSearchParams(window.location.search).get("kind") === "ryder" ? "ryder" : "store");
  const [ryderPositions, setRyderPositions] = useState({});

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [companyRating, setCompanyRating] = useState(5);
  const [companyComment, setCompanyComment] = useState("");
  const [ryderRating, setRyderRating] = useState(5);
  const [ryderComment, setRyderComment] = useState("");
  const [productRatings, setProductRatings] = useState({});

  const socketRef = useRef(null);
  const ordersRef = useRef([]);
  ordersRef.current = orders;

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  // Connect socket once on mount
  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"], auth: { token } });
    socketRef.current = socket;

    socket.on("ryder:location", ({ orderId, lat, lng }) => {
      setRyderPositions((prev) => ({ ...prev, [orderId]: { lat, lng } }));
    });

    socket.on("ryder:nearby", ({ orderId }) => {
      toast.info("¡Tu domiciliario está por llegar! Prepárate para recibir tu pedido.", {
        icon: "🛵",
        autoClose: 8000,
      });
    });

    socket.on("chat:message", ({ senderRole, message }) => {
      if (senderRole === "company") {
        toast.info(`💬 La tienda te escribió: ${message}`, { autoClose: 7000 });
      } else if (senderRole === "ryder") {
        toast.info(`💬 El domiciliario te escribió: ${message}`, { autoClose: 7000 });
      }
    });

    socket.on("order:status", ({ orderId, status }) => {
      const order = ordersRef.current.find((o) => o.id === orderId);
      const isPickup = order?.deliveryType === "recoger_en_tienda";

      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));

      const toastMap = {
        confirmado: { msg: "Tu pedido fue confirmado por la tienda", icon: "✅" },
        asignado:   { msg: "Un domiciliario fue asignado a tu pedido", icon: "🛵" },
        en_camino:  { msg: isPickup ? "¡Tu pedido está listo para retirar!" : "Tu pedido está en camino", icon: isPickup ? "🏪" : "🚀" },
        entregado:  { msg: isPickup ? "¡Pedido listo! Ya puedes recogerlo" : "¡Tu pedido fue entregado!", icon: isPickup ? "✅" : "📦" },
        cancelado:  { msg: "Tu pedido fue cancelado", icon: "❌" },
      };
      const t = toastMap[status];
      if (t) toast.info(t.msg, { icon: t.icon, autoClose: 6000 });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Subscribe to active order rooms whenever orders load/change
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    orders
      .filter((o) => ["asignado", "en_camino"].includes(o.status))
      .forEach((o) => socket.emit("order:subscribe", o.id));
  }, [orders]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`/orders/user/${user.id}`);
      const list = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      setOrders(list);
      // Auto-switch to history if there are no active orders
      const hasActive = list.some((o) => ["pendiente", "confirmado", "asignado", "en_camino"].includes(o.status));
      if (!hasActive && list.length > 0) setTab("history");
      // Pre-seed ryder positions from last known DB coordinates
      const initial = {};
      list.forEach((o) => {
        if (["asignado", "en_camino"].includes(o.status) && o.ryder?.lat && o.ryder?.lng) {
          const lat = Number(o.ryder.lat);
          const lng = Number(o.ryder.lng);
          if (lat !== 0 && lng !== 0) initial[o.id] = { lat, lng };
        }
      });
      if (Object.keys(initial).length > 0) {
        setRyderPositions((prev) => ({ ...initial, ...prev }));
      }
    } catch {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const openRatingModal = (order) => {
    setSelectedOrder(order);
    setCompanyRating(5);
    setCompanyComment("");
    setRyderRating(5);
    setRyderComment("");
    const initial = {};
    order.items.forEach((item) => {
      if (item.productId) initial[item.productId] = { rating: 0, comment: "" };
    });
    setProductRatings(initial);
    setShowRatingModal(true);
  };

  const setProductRating = (productId, field, value) => {
    setProductRatings((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
  };

  const activeStatuses = ["pendiente", "confirmado", "asignado", "en_camino"];
  const historicStatuses = ["entregado", "cancelado"];

  const filteredOrders = orders.filter((o) =>
    tab === "active"
      ? activeStatuses.includes(o.status)
      : historicStatuses.includes(o.status),
  );

  const toggleAccordion = (id) =>
    setOpenOrderId(openOrderId === id ? null : id);

  const getStatusBadge = (status, deliveryType) => {
    const isPickup = deliveryType === "recoger_en_tienda";
    const map = {
      pendiente:  { label: "Pendiente",           icon: Clock,        bg: "bg-zinc-800 text-zinc-300"           },
      confirmado: { label: isPickup ? "Preparando tu pedido" : "Confirmado",
                                                   icon: CheckCircle,  bg: "bg-blue-950/60 text-blue-400"        },
      asignado:   { label: "Asignado",             icon: UserCheck,    bg: "bg-yellow-950/60 text-yellow-400"    },
      en_camino:  { label: isPickup ? "¡Listo para retirar!" : "En camino",
                                                   icon: isPickup ? PackageCheck : Truck,
                                                                       bg: isPickup ? "bg-amber-950/60 text-amber-400" : "bg-violet-950/60 text-violet-400" },
      entregado:  { label: isPickup ? "Retirado"  : "Entregado",
                                                   icon: PackageCheck, bg: "bg-green-950/60 text-green-400"      },
      cancelado:  { label: "Cancelado",            icon: XCircle,      bg: "bg-red-950/60 text-red-400"          },
    };
    const b = map[status] ?? map.cancelado;
    const Icon = b.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${b.bg}`}>
        <Icon size={14} /> {b.label}
      </span>
    );
  };

  const StarRow = ({ value, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={26}
          onClick={() => onChange(s)}
          className={
            s <= value
              ? "text-yellow-500 fill-yellow-500 cursor-pointer"
              : "text-gray-300 cursor-pointer"
          }
        />
      ))}
    </div>
  );

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("¿Cancelar este pedido? Solo puedes cancelarlo antes de que la tienda lo acepte.")) return;
    try {
      await axios.patch(`/orders/${orderId}/cancel`);
      toast.success("Pedido cancelado");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo cancelar el pedido");
    }
  };

  const handleRateOrder = async () => {
    try {
      const payload = { companyScore: companyRating, companyComment };
      if (selectedOrder.ryder) {
        payload.ryderScore = ryderRating;
        payload.ryderComment = ryderComment;
      }
      await axios.patch(`/orders/${selectedOrder.id}/rate`, payload);

      const reviewEntries = Object.entries(productRatings).filter(
        ([, v]) => v.rating > 0
      );
      for (const [productId, { rating: pRating, comment: pComment }] of reviewEntries) {
        try {
          await axios.post("/reviews", {
            productId,
            rating: pRating,
            comment: pComment.trim() || "Sin comentario",
          });
        } catch {
          // already reviewed or error — skip silently
        }
      }

      toast.success("¡Gracias por tu calificación!");
      setShowRatingModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch {
      toast.error("Error al calificar");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-black">
        <Loader className="animate-spin text-supernova-500" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto pb-24">
      <button
        onClick={() => navigate(-1)}
        className="flex gap-2 mb-6 text-supernova-400 font-semibold"
      >
        <ArrowLeft /> Volver
      </button>

      <h1 className="text-3xl font-bold mb-6 text-white">Mis Pedidos</h1>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("active")}
          className={`flex-1 py-2.5 rounded-lg font-semibold transition ${
            tab === "active" ? "bg-supernova-500 text-black" : "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
          }`}
        >
          Activos
          {orders.filter((o) => activeStatuses.includes(o.status)).length > 0 && (
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
              tab === "active" ? "bg-black/20 text-black" : "bg-zinc-700 text-gray-300"
            }`}>
              {orders.filter((o) => activeStatuses.includes(o.status)).length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("history")}
          className={`flex-1 py-2.5 rounded-lg font-semibold transition ${
            tab === "history" ? "bg-supernova-500 text-black" : "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
          }`}
        >
          Historial
          {orders.filter((o) => historicStatuses.includes(o.status)).length > 0 && (
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
              tab === "history" ? "bg-black/20 text-black" : "bg-zinc-700 text-gray-300"
            }`}>
              {orders.filter((o) => historicStatuses.includes(o.status)).length}
            </span>
          )}
        </button>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 p-10 rounded-xl text-center">
          <ShoppingBag size={48} className="mx-auto text-zinc-600 mb-4" />
          <p className="text-gray-400">No hay pedidos aquí</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden"
            >
              <button
                onClick={() => toggleAccordion(order.id)}
                className="w-full p-4 flex justify-between items-center hover:bg-zinc-800/50 transition"
              >
                <div className="text-left">
                  <p className="font-bold text-white">Pedido #{order.orderNumber}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(order.createdAt).toLocaleString("es-CO")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(order.status, order.deliveryType)}
                  {openOrderId === order.id
                    ? <ChevronUp className="text-gray-400" />
                    : <ChevronDown className="text-gray-400" />}
                </div>
              </button>

              {openOrderId === order.id && (
                <div className="p-4 border-t border-zinc-700 space-y-4">
                  {order.company && (
                    <div className="flex items-center gap-3">
                      {order.company.logo && (
                        <img
                          src={getImageUrl(order.company.logo)}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-white flex items-center gap-1">
                          {order.company.name}
                          {order.company.verified && (
                            <BadgeCheck size={14} className="text-blue-400" />
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="font-semibold mb-2 text-gray-200">Productos</p>
                    {order.items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-zinc-800 p-2 rounded-lg mb-1"
                      >
                        {item.image && (
                          <img
                            src={getImageUrl(item.image)}
                            alt={item.title}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white">
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-400">
                            Cantidad: {item.quantity}
                          </p>
                          {item.note && (
                            <p className="text-xs text-amber-400 italic mt-0.5">📝 {item.note}</p>
                          )}
                        </div>
                        <p className="text-sm font-bold text-gray-300">
                          ${(item.price * item.quantity).toLocaleString("es-CO")}
                        </p>
                      </div>
                    ))}
                  </div>

                  {order.deliveryType === "recoger_en_tienda" ? (
                    <div className="bg-blue-950/40 p-3 rounded-lg border border-blue-900/50">
                      <p className="font-semibold flex gap-2 items-center text-blue-300">
                        <MapPin size={16} /> Retiro en tienda
                        <span className="text-[10px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded-full">
                          PICKUP
                        </span>
                      </p>
                      {order.pickupSede && (
                        <>
                          <p className="text-sm font-medium text-white mt-1">{order.pickupSede.name}</p>
                          {order.pickupSede.address && (
                            <p className="text-xs text-gray-400">{order.pickupSede.address}</p>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="bg-amber-950/30 p-3 rounded-lg border border-amber-900/30">
                      <p className="font-semibold flex gap-2 items-center text-amber-300">
                        <MapPin size={16} /> Dirección de entrega
                      </p>
                      {order.deliveryAddress && <p className="text-sm text-gray-300 mt-1">{order.deliveryAddress}</p>}
                      {order.deliveryCity && <p className="text-xs text-gray-400">{order.deliveryCity}</p>}
                    </div>
                  )}

                  {order.status === "pendiente" && (
                    <button
                      onClick={() => handleCancelOrder(order.id)}
                      className="w-full py-2 rounded-xl border border-red-800 text-red-400 text-sm font-semibold hover:bg-red-950/30 transition"
                    >
                      Cancelar pedido
                    </button>
                  )}

                  {order.ryder && (
                    <div className="p-3 bg-blue-950/30 rounded-lg border border-blue-900/30">
                      <p className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-2">
                        <Bike size={16} className="text-blue-400" />
                        Domiciliario
                      </p>
                      <div className="flex items-center gap-3 mb-1">
                        {order.ryder.avatar ? (
                          <img src={getImageUrl(order.ryder.avatar)} alt={order.ryder.user?.name}
                            className="w-12 h-12 rounded-full object-cover border border-blue-900/40 flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-900/40 flex items-center justify-center text-white font-bold flex-shrink-0">
                            {order.ryder.user?.name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <p className="text-sm text-white flex-1 min-w-0 truncate">{order.ryder.user?.name}</p>
                        {order.ryder.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="text-yellow-500" size={16} />
                            <span className="text-sm font-bold text-white">
                              {Number(order.ryder.rating).toFixed(1)}
                            </span>
                          </div>
                        )}
                      </div>

                      {order.status === "en_camino" && (
                        ryderPositions[order.id] ? (
                          <>
                            <p className="text-xs text-blue-400 flex items-center gap-1 mb-1">
                              <Navigation size={12} className="animate-pulse" />
                              Ubicación en tiempo real
                            </p>
                            <RyderLiveMap
                              position={ryderPositions[order.id]}
                              vehicleType={order.ryder.vehicleType}
                              deliveryLocation={
                                order.deliveryLat && order.deliveryLng
                                  ? { lat: Number(order.deliveryLat), lng: Number(order.deliveryLng) }
                                  : null
                              }
                            />
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Navigation size={12} />
                            Esperando ubicación del domiciliario...
                          </p>
                        )
                      )}
                    </div>
                  )}

                  {order.status === "entregado" && (
                    <div className="mt-4">
                      {order.companyRatingScore ? (
                        <div className="p-4 bg-green-950/30 rounded-lg border border-green-900/30 space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-200 mb-1">
                              Calificación a la empresa
                            </p>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={18}
                                  className={
                                    star <= order.companyRatingScore
                                      ? "text-yellow-500 fill-yellow-500"
                                      : "text-zinc-600"
                                  }
                                />
                              ))}
                            </div>
                            {order.companyRatingComment && (
                              <p className="text-sm text-gray-400 italic mt-1">
                                "{order.companyRatingComment}"
                              </p>
                            )}
                          </div>
                          {order.ryderRatingScore > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-gray-200 mb-1">
                                Calificación al domiciliario
                              </p>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={18}
                                    className={
                                      star <= order.ryderRatingScore
                                        ? "text-yellow-500 fill-yellow-500"
                                        : "text-zinc-600"
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => openRatingModal(order)}
                          className="w-full bg-supernova-500 text-black py-2 rounded-lg font-semibold"
                        >
                          Calificar Pedido
                        </button>
                      )}
                    </div>
                  )}

                  <div className="bg-zinc-800 rounded-lg p-4 space-y-1 text-gray-300">
                    <p className="font-semibold text-gray-200 mb-2">Resumen de pago</p>
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>${Number(order.subtotal).toLocaleString("es-CO")}</span>
                    </div>
                    {order.couponCode && Number(order.couponDiscount) > 0 && (
                      <div className="flex justify-between text-sm text-green-400 font-semibold">
                        <span>Cupón ({order.couponCode})</span>
                        <span>- ${Number(order.couponDiscount).toLocaleString("es-CO")}</span>
                      </div>
                    )}
                    {order.qpointsUsed > 0 && (
                      <div className="flex justify-between text-sm text-green-400 font-semibold">
                        <span>QPUNTOS usados</span>
                        <span>− ${order.qpointsUsed.toLocaleString("es-CO")}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Domicilio</span>
                      <span>${Number(order.deliveryFee).toLocaleString("es-CO")}</span>
                    </div>
                    <hr className="border-zinc-700 my-1" />
                    <div className="flex justify-between font-bold text-base">
                      <span className="text-white">Total</span>
                      <span className="text-supernova-400">
                        ${Number(order.total).toLocaleString("es-CO")}
                      </span>
                    </div>
                  </div>

                  {!["entregado", "cancelado"].includes(order.status) && (() => {
                    const kind = order.ryder ? chatTab : "store";
                    return (
                      <>
                        {order.ryder && (
                          <div className="flex gap-2 mb-3">
                            {[["store", "Tienda"], ["ryder", "Domiciliario"]].map(([k, label]) => (
                              <button key={k} type="button" onClick={() => setChatTab(k)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  chatTab === k ? "bg-supernova-500 text-black" : "bg-white/10 text-white/70"
                                }`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                        <OrderChat
                          key={`${order.id}-${kind}`}
                          orderId={order.id}
                          kind={kind}
                          user={{ id: user?.id, role: "user" }}
                          isActive={true}
                        />
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showRatingModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 pb-4 border-b border-zinc-700">
              <h2 className="font-bold text-xl text-white">Calificar Pedido</h2>
              <button onClick={() => setShowRatingModal(false)} className="text-gray-400 hover:text-white transition">
                <X />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <p className="font-semibold text-white mb-2">
                  {selectedOrder.company?.name ?? "Empresa"}
                </p>
                <StarRow value={companyRating} onChange={setCompanyRating} />
                <textarea
                  value={companyComment}
                  onChange={(e) => setCompanyComment(e.target.value)}
                  className="w-full border border-zinc-600 bg-zinc-800 text-white rounded-lg p-3 mt-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-supernova-400 placeholder-zinc-500"
                  rows={2}
                  placeholder="Comentario sobre la empresa (opcional)"
                />
              </div>

              {selectedOrder.ryder && (
                <div>
                  <p className="font-semibold text-white mb-2">
                    Domiciliario: {selectedOrder.ryder.user?.name}
                  </p>
                  <StarRow value={ryderRating} onChange={setRyderRating} />
                  <textarea
                    value={ryderComment}
                    onChange={(e) => setRyderComment(e.target.value)}
                    className="w-full border border-zinc-600 bg-zinc-800 text-white rounded-lg p-3 mt-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-supernova-400 placeholder-zinc-500"
                    rows={2}
                    placeholder="Comentario sobre el domiciliario (opcional)"
                  />
                </div>
              )}

              {selectedOrder.items.some((i) => i.productId) && (
                <div>
                  <p className="font-semibold text-white mb-3">
                    Reseñas de productos{" "}
                    <span className="text-xs text-gray-400 font-normal">(opcional)</span>
                  </p>
                  <div className="space-y-4">
                    {selectedOrder.items
                      .filter((i) => i.productId)
                      .map((item) => (
                        <div key={item.productId} className="border border-zinc-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            {item.image && (
                              <img
                                src={getImageUrl(item.image)}
                                alt={item.title}
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <p className="text-sm font-medium text-white">
                              {item.title}
                            </p>
                          </div>
                          <div className="flex gap-1 mb-2">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                size={22}
                                onClick={() => setProductRating(item.productId, "rating", s)}
                                className={
                                  s <= (productRatings[item.productId]?.rating ?? 0)
                                    ? "text-yellow-500 fill-yellow-500 cursor-pointer"
                                    : "text-zinc-600 cursor-pointer"
                                }
                              />
                            ))}
                            {productRatings[item.productId]?.rating > 0 && (
                              <button
                                onClick={() => setProductRating(item.productId, "rating", 0)}
                                className="ml-2 text-xs text-gray-400 hover:text-gray-200"
                              >
                                quitar
                              </button>
                            )}
                          </div>
                          {productRatings[item.productId]?.rating > 0 && (
                            <textarea
                              value={productRatings[item.productId]?.comment ?? ""}
                              onChange={(e) =>
                                setProductRating(item.productId, "comment", e.target.value)
                              }
                              className="w-full border border-zinc-600 bg-zinc-800 text-white rounded p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-supernova-400 placeholder-zinc-500"
                              rows={2}
                              placeholder="¿Qué te pareció este producto? (opcional)"
                            />
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleRateOrder}
                className="w-full bg-supernova-500 text-black py-3 rounded-lg font-bold"
              >
                Enviar calificación
              </button>
            </div>
          </div>
        </div>
      )}

      <MobileBottomBar />
    </div>
  );
}
